import React, { useState, useEffect, useContext } from "react";
import { useLocation } from "react-router-dom";
import { SocketContext } from "../context/SocketProvider";
import { DEFAULT_ROOM } from "../constants/gameStages";
import { FaChevronRight } from "react-icons/fa6";
import { useLanguage } from "../context/LanguageContext";

export const SimplePopup = ({ isOpen, onClose, onConfirm, isInitiator, type }) => {
  const { t, language } = useLanguage();
  if (!isOpen) return null;

  const getHeader = () => {
    if (type === 'quit') {
      return isInitiator ? t('waitingOtherPlayerQuit') : t('wantToQuit');
    }
    return isInitiator ? t('waitingOtherPlayer') : t('wantToContinue');
  };

  const getConfirmText = () => {
    if (type === 'quit') {
      return t("button.newGame", "QUIT GAME");
    }
    return t("button.nextRound", "NEXT ROUND");
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      {/* Container */}
      <div className="bg-white w-[514px] rounded-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200 flex flex-col">
        {/* Yellow Header */}
        <div className="bg-yellow400 w-[514px] h-[84px] px-8 text-white text-center font-bold text-[19px] uppercase leading-tight flex items-center justify-center">
          {getHeader()}
        </div>

        {/* White Body */}
        <div className="bg-white w-[514px] h-[117px] flex flex-col justify-end pb-[24px]">
          {/* Button */}
          <div className="flex flex-row justify-end gap-[24px] px-[24px] w-full">
            {isInitiator ? (
              <button
                onClick={onClose}
                className="btn-results-sm-transparent transition-all active:scale-95 relative"
              >
                <span className="btn-text-pos-right-sm">{t("cancel", "cancel")}</span>
                <FaChevronRight className="arrow-pos-right-sm" style={{strokeWidth: '2.5'}} />
              </button>
            ) : (
              <>
                <button
                  onClick={onClose}
                  className="btn-results-sm-transparent transition-all active:scale-95 relative"
                >
                  <span className="btn-text-pos-right-sm">{t("cancel", "cancel")}</span>
                  <FaChevronRight className="arrow-pos-right-sm" style={{strokeWidth: '2.5'}} />
                </button>
                <button
                  onClick={() => {
                    onConfirm();
                    onClose();
                  }}
                  className="btn-results-sm transition-all shadow-md active:scale-95 relative"
                >
                  <span className="btn-text-pos-right-sm">{getConfirmText()}</span>
                  <FaChevronRight className="arrow-pos-right-sm" style={{strokeWidth: '2.5'}} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * GameEventPopup for listening to the current stage of game
 */
const GameEventPopup = () => {
  const { socket } = useContext(SocketContext);
  const location = useLocation();
  const [popupData, setPopupData] = useState({ 
    isOpen: false, 
    isInitiator: false,
    type: 'next_round'
  });

  // Close popup
  useEffect(() => {
    setPopupData(prev => ({ ...prev, isOpen: false }));
  }, [location.pathname]);

  useEffect(() => {
    if (!socket) return;

    const handleShowPopup = (data) => {
      if (location.pathname !== '/points') return;

      setPopupData({
        isOpen: true,
        isInitiator: data.is_initiator,
        type: data.type || 'next_round'
      });
    };

    const handleClosePopup = () => {
      setPopupData(prev => ({ ...prev, isOpen: false }));
    };

    socket.on("show_role_popup", handleShowPopup);
    socket.on("next_round_confirmed", handleClosePopup);
    socket.on("new_game_confirmed", handleClosePopup);
    socket.on("game_stage_updated", handleClosePopup);
    socket.on("next_round_vote_cancelled", handleClosePopup);
    socket.on("new_game_vote_cancelled", handleClosePopup);
    socket.on("hide_role_popup", handleClosePopup);

    return () => {
      socket.off("show_role_popup", handleShowPopup);
      socket.off("next_round_confirmed", handleClosePopup);
      socket.off("new_game_confirmed", handleClosePopup);
      socket.off("game_stage_updated", handleClosePopup);
      socket.off("next_round_vote_cancelled", handleClosePopup);
      socket.off("new_game_vote_cancelled", handleClosePopup);
      socket.off("hide_role_popup", handleClosePopup);
    };
  }, [socket, location.pathname]);

  const handleConfirm = () => {
    if (popupData.type === 'quit') {
      socket.emit("reset_room", { room: DEFAULT_ROOM, resetMode: true });
    } else {
      socket.emit("next_round", { room: DEFAULT_ROOM });
    }
  };

  const handleClose = () => {
    setPopupData(prev => ({ ...prev, isOpen: false }));
    socket.emit("cancel_next_round_vote", { room: DEFAULT_ROOM });
    socket.emit("cancel_new_game_vote", { room: DEFAULT_ROOM });
    socket.emit("cancel_role_popup", { room: DEFAULT_ROOM });
  };

  return (
    <SimplePopup
      isOpen={popupData.isOpen}
      isInitiator={popupData.isInitiator}
      type={popupData.type}
      onClose={handleClose}
      onConfirm={handleConfirm}
    />
  );
};

export default GameEventPopup;
